import { createAsyncThunk } from "@reduxjs/toolkit";
import { HttpService } from "../../modules/shared/services/http.service";
import { GlobalService } from "../../modules/shared/services/global.service";
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import i18n from "../../i18n";

// ==================== INTERFACES ====================

export interface AchievementConfig {
  id: string;
  configKey: string;
  name: string;
  thresholds: {
    // Professional thresholds
    topEarnerAmount?: number;
    expertProjectsCount?: number;
    risingStarProjectsCount?: number;
    risingStarMinRating?: number;
    highlyRatedReviewsCount?: number;
    highlyRatedMinRating?: number;
    
    // Client thresholds
    topClientSpentAmount?: number;
    topClientSuccessRate?: number;
    powerClientProjectsCount?: number;
    teamBuilderProjectsCount?: number;
    teamBuilderSuccessRate?: number;
    trustedReviewerCount?: number;
  };
  isEnabled: boolean;
}

// ==================== CONFIG ACTIONS ====================

// Get achievement config by key
export const getAchievementConfig = createAsyncThunk<
  AchievementConfig,
  string,
  { rejectValue: string }
>("achievements/getConfig", async (configKey, { rejectWithValue, dispatch }) => {
  try {
    const response = await HttpService.get(`api/v1/achievements/config/${configKey}`, true);
    return response;
  } catch (error: any) {
    GlobalService.showToast(
      "error",
      i18n.t("toast.achievements.configFetchFailed"),
      error?.response?.data?.message || i18n.t("toast.achievements.configFetchFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch configuration"
    );
  }
});

// Get all achievement configs
export const getAllAchievementConfigs = createAsyncThunk<
  AchievementConfig[],
  void,
  { rejectValue: string }
>("achievements/getAllConfigs", async (_, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.get("api/v1/achievements/config", true);
    dispatch(hideLoader());
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.achievements.configsFetchFailed"),
      error?.response?.data?.message || i18n.t("toast.achievements.configsFetchFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch configurations"
    );
  }
});

// Update achievement config
export const updateAchievementConfig = createAsyncThunk<
  AchievementConfig,
  { configKey: string; thresholds: any },
  { rejectValue: string }
>("achievements/updateConfig", async ({ configKey, thresholds }, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.patch(
      `api/v1/achievements/config/${configKey}`,
      { thresholds },
      true
    );
    dispatch(hideLoader());
    GlobalService.showToast(
      "success",
      i18n.t("toast.achievements.configUpdated"),
      i18n.t("toast.achievements.configUpdatedMessage")
    );
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.achievements.configUpdateFailed"),
      error?.response?.data?.message || i18n.t("toast.achievements.configUpdateFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to update configuration"
    );
  }
});

